import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  TextField,
  Divider,
  DialogActions
} from '@material-ui/core';

import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import CameraIcon from '@material-ui/icons/Camera';
import ViewCarouselIcon from '@material-ui/icons/ViewCarousel';

import Webcam from 'react-webcam';

import { useFaceRegistration } from './useFaceRegistration';

const FaceRegistration = () => {
  const {
    webcamRef,
    canvasRef,
    handleRegisterFace,
    registeredFaces,
    handleSaveImages,
    bioID,
    setBioID,
    data,
    imgCount
  } = useFaceRegistration();

  const [openDialog, setOpenDialog] = useState(false);
  const [viewImages, setViewImages] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const handleOpenPreview = index => {
    setPreviewIndex(index);
    setOpenPreview(true);
  };

  const handleNext = () => {
    setPreviewIndex(prev => Math.min(prev + 1, registeredFaces.length - 1));
  };

  const handlePrev = () => {
    setPreviewIndex(prev => Math.max(prev - 1, 0));
  };

  return (
    <Box width="100%">
      {/* MAIN BOX */}
      <Box
        onDoubleClick={() => setOpenDialog(true)}
        style={{
          border: '1px solid #ccc',
          borderRadius: 12,
          padding: 16,
          width: '100%'
        }}
      >
        {data?.value?.images?.length > 0 ? (
          <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
            {data.value.images.map((img, i) => (
              <Box
                key={i}
                onDoubleClick={() => {
                  setSelectedImg(img);
                  setViewImages(true);
                }}
                style={{
                  position: 'relative',
                  padding: 4,
                  borderRadius: 10,
                  background: '#f5f5f5',
                  cursor: 'pointer'
                }}
              >
                <img
                  src={img}
                  alt={`face-${i}`}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 8,
                    objectFit: 'cover'
                  }}
                />

                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedImg(img);
                    setViewImages(true);
                  }}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4
                  }}
                >
                  <AspectRatioIcon fontSize="small" color="primary" />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Box display="flex" justifyContent="center">
            <AddAPhotoIcon
              fontSize="large"
              color="primary"
              onClick={() => setOpenDialog(true)}
              style={{ cursor: 'pointer' }}
            />
          </Box>
        )}
      </Box>

      {/* VIEW IMAGE DIALOG */}
      <Dialog
        open={viewImages}
        onClose={() => setViewImages(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20
          }}
        >
          {selectedImg && (
            <img
              src={selectedImg}
              alt="preview"
              style={{
                maxWidth: '100%',
                maxHeight: 500,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 10
              }}
            />
          )}
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button onClick={() => setViewImages(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* CAMERA DIALOG */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent style={{ padding: 0 }}>
          {/* CAMERA */}
          <Box
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4/3',
              overflow: 'hidden',
              background: '#000'
            }}
          >
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/png"
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />

            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%'
              }}
            />
          </Box>

          {/* CONTROLS */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={2}
            style={{ padding: '0 10px' }}
          >
            {/* PREVIEW THUMB */}
            <Box
              onClick={() =>
                registeredFaces.length &&
                handleOpenPreview(registeredFaces.length - 1)
              }
              style={{
                width: 60,
                height: 60,
                background: '#f5f5f5',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                overflow: 'hidden'
              }}
            >
              {registeredFaces?.length > 0 ? (
                <img
                  src={registeredFaces[registeredFaces.length - 1]}
                  alt="latest"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <ViewCarouselIcon style={{ opacity: 0.4 }} />
              )}
            </Box>

            {/* CAPTURE BUTTON */}
            <Button
              onClick={handleRegisterFace}
              variant="outlined"
              color="primary"
              style={{
                borderRadius: '50%',
                minWidth: 70,
                width: 70,
                height: 70
              }}
            >
              <CameraIcon style={{ fontSize: 50 }} />
            </Button>

            {/* BIO ID */}
            <Box style={{ width: 140 }} marginLeft={-4}>
              <TextField
                type="number"
                value={bioID}
                label="Bio ID"
                size="small"
                variant="outlined"
                fullWidth
                error={!bioID}
                helperText={!bioID ? 'Required' : ''}
                onChange={e => setBioID(e.target.value)}
              />
            </Box>
          </Box>

          {/* SAVE BUTTON */}
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="primary"
            onClick={() => {
              handleSaveImages();
              setOpenDialog(false);
            }}
            style={{ marginTop: 10 }}
            disabled={registeredFaces.length !== Number(imgCount) || !bioID}
          >
            Proceed ({registeredFaces.length}/{imgCount})
          </Button>
        </DialogContent>
      </Dialog>

      {/* PREVIEW DIALOG */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}
        >
          {registeredFaces?.length > 0 && (
            <>
              <img
                src={registeredFaces[previewIndex]}
                alt={`preview-${previewIndex}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: 500,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 10
                }}
              />

              <Box
                display="flex"
                justifyContent="space-between"
                width="100%"
                mt={2}
              >
                <Button
                  onClick={handlePrev}
                  disabled={previewIndex === 0}
                  color="primary"
                >
                  Prev
                </Button>

                <Button onClick={() => setOpenPreview(false)}>Close</Button>

                <Button
                  onClick={handleNext}
                  color="primary"
                  disabled={previewIndex === registeredFaces.length - 1}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FaceRegistration;
